import { Col, Container, Row } from 'react-bootstrap'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../../common/integrations'
import React, { useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../../schema'
import { useGetIntegrationAdminSettings } from '../../../hooks'
import { BUSINESS_EVENT_GROUP_MAPPING, BusinessEventType } from '../../../../../../../models/BusinessEvent'
import { BusinessEventsRow } from './BusinessEventsRow'
import { GroupCheckbox } from '../../../../../../../components/GroupCheckbox/GroupCheckbox'

export interface BusinessEventsAdminSettingsProps {
  serviceId: IntegrationServiceId
}

export const BusinessEventsMappingSettings: React.FC<BusinessEventsAdminSettingsProps> = ({ serviceId }) => {
  const { isAccountFetching } = useGetIntegrationAdminSettings(serviceId)
  const { watch, setValue } = useFormContext<IntegrationSettings>()
  const enabledBusinessEventTypes = (watch('enabledBusinessEventTypes') ?? []) as BusinessEventType[]

  const allEvents = useMemo(() => Object.values(BUSINESS_EVENT_GROUP_MAPPING).flat(), [])

  const handleGroupToggle = useCallback(
    (options: string[], allEnabled: boolean) => {
      const events = options as BusinessEventType[]
      const current = (watch('enabledBusinessEventTypes') ?? []) as BusinessEventType[]
      if (allEnabled) {
        setValue(
          'enabledBusinessEventTypes',
          current.filter((t) => !events.includes(t as BusinessEventType)),
          { shouldDirty: true, shouldValidate: true },
        )
      } else {
        const eventsToEnable = events.filter((t) => !current.includes(t))
        setValue('enabledBusinessEventTypes', [...current, ...eventsToEnable], { shouldDirty: true, shouldValidate: true })
      }
    },
    [watch, setValue],
  )

  if (isAccountFetching) {
    return (
      <Container>
        <div className='skeleton pt-2' style={{ width: '100%', height: '60px' }} />
        <div className='skeleton pt-2' style={{ width: '100%', height: '60px' }} />
      </Container>
    )
  }

  return (
    <Container className='py-3 gap-4'>
      <Row className='mb-6'>
        <Col>
          <h3 className='mb-2'>Client signals</h3>
          <div className='zocks-text-gray-900'>
            Choose which 'Client signals' to save to {getIntegrationDisplayName(serviceId)}. For each one, set up the field mapping so data lands in the right
            place.
          </div>
        </Col>
      </Row>
      <Row className='mb-4'>
        <Col>
          <div className='d-flex align-items-center gap-2'>
            <GroupCheckbox options={allEvents} selectedOptions={enabledBusinessEventTypes} onToggle={handleGroupToggle} />
            <span className='paragraph-medium'>Select all</span>
          </div>
        </Col>
      </Row>
      <Row>
        <div className='d-flex flex-column gap-4'>
          {Object.entries(BUSINESS_EVENT_GROUP_MAPPING).map(([groupKey, events]) => (
            <div key={groupKey} className='d-flex flex-column gap-2'>
              <div className='d-flex align-items-center gap-2'>
                <GroupCheckbox options={events as string[]} selectedOptions={enabledBusinessEventTypes as string[]} onToggle={handleGroupToggle} />
                <div className='lead-small'>{groupKey}</div>
              </div>

              {events.map((event, index) => (
                <BusinessEventsRow serviceId={serviceId} key={groupKey + event + index} businessEventType={event} />
              ))}
            </div>
          ))}
        </div>
      </Row>
    </Container>
  )
}
